import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { useLang } from '../i18n/LanguageContext';

const WEB3FORMS_ACCESS_KEY = '712e1c60-f005-4ef3-a7ab-a8a9d8343405';

export const Contact = () => {
  const { t } = useLang();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSending, setIsSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error(t.contact.errorFill);
      return;
    }
    setIsSending(true);
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: `Portfolio · Mensaje de ${formData.name}`,
          from_name: 'Portfolio Raúl de la Fuente',
          name: formData.name,
          email: formData.email,
          message: formData.message,
          replyto: formData.email,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.message || t.contact.error);
      }
      toast.success(t.contact.success);
      setFormData({ name: '', email: '', message: '' });
    } catch (err) {
      toast.error(err.message || t.contact.error);
    } finally {
      setIsSending(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="py-8 lg:py-12">
      <div className="section-container">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl font-semibold mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              {t.contact.title}
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">{t.contact.intro}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <div className="text-center md:text-left space-y-2">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                {t.contact.emailLabel}
              </h3>
              <a
                href="mailto:r.delafuentepp@gmail.com"
                className="text-xl font-medium text-accent hover:text-accent-hover transition-colors inline-block"
              >
                r.delafuentepp@gmail.com
              </a>
            </div>

            <div className="text-center md:text-left space-y-2">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                {t.contact.linkedInLabel}
              </h3>
              <a
                href="https://www.linkedin.com/in/ra%C3%BAl-de-la-fuente-p%C3%A9rez-puelles-21703630/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xl font-medium text-accent hover:text-accent-hover transition-colors inline-block"
              >
                {t.contact.linkedInCta}
              </a>
            </div>
          </div>

          <div className="bg-card border border-border rounded-sm p-8 lg:p-12">
            <h3 className="text-2xl font-semibold mb-6 text-foreground">{t.contact.formTitle}</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <input type="checkbox" name="botcheck" style={{ display: 'none' }} tabIndex="-1" autoComplete="off" />

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                  {t.contact.name}
                </label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t.contact.namePh}
                  className="w-full border-border bg-background focus:border-accent focus:ring-accent"
                  required
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                  {t.contact.email}
                </label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t.contact.emailPh}
                  className="w-full border-border bg-background focus:border-accent focus:ring-accent"
                  required
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
                  {t.contact.message}
                </label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t.contact.messagePh}
                  rows={6}
                  className="w-full border-border bg-background focus:border-accent focus:ring-accent resize-none"
                  required
                  data-testid="contact-message-input"
                />
              </div>

              <Button
                type="submit"
                size="lg"
                disabled={isSending}
                className="w-full bg-accent hover:bg-accent-hover text-accent-foreground font-medium py-6 text-base rounded-sm transition-all hover:shadow-lg disabled:opacity-60"
                data-testid="contact-submit-btn"
              >
                {isSending ? t.contact.sending : t.contact.send}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
